import { ConversionFactors } from "shared/types";
import { TemperatureUnitsType } from "store/recipe/recipe.types";
import { capitalizeFirstLetter, numberToFixedPointNotation } from "./string";
import { isNotEmpty } from "./validate";
const temperatureUnitsRegex = new RegExp("^(Celsius|Fahrenheit)(Per(?:Hour[0-9]?|Min(?:ute)?[0-9]?|Sec(?:ond)?[0-9]?))?$");
const nonTemperatureUnitsRegex = new RegExp("^(.*?)(Per(?:Liter|Milliliter|Microliter))?(Per(?:Hour[0-9]?|Min(?:ute)?[0-9]?|Sec(?:ond)?[0-9]?))?$");
export var FinalFormMutators;
(function (FinalFormMutators) {
    FinalFormMutators["resetNestedFieldsTouchedMutator"] = "resetNestedFieldsTouchedMutator";
    FinalFormMutators["resetFieldsTouchedMutator"] = "resetFieldsTouchedMutator";
})(FinalFormMutators || (FinalFormMutators = {}));
export const resetFieldsTouchedMutator = (args, { fields }) => {
    args.forEach((name) => !!fields[name] && (fields[name].touched = false));
};
export const resetNestedFieldsTouchedMutator = (args, { fields }) => {
    const parentFieldName = args[0];
    const filteredFieldKeys = Object.keys(fields).filter((k) => k.indexOf(parentFieldName) === 0);
    filteredFieldKeys.forEach((k) => {
        fields[k].touched = false;
    });
};
const getConversionFactor = (unit) => ConversionFactors[capitalizeFirstLetter(unit)];
export const convertValueBasedOnUnits = (value, prevUnits, newUnits) => {
    if (isNaN(Number(value ?? undefined)) || prevUnits === newUnits || !prevUnits || !newUnits) {
        return value;
    }
    let valueToSave = value;
    const prevTemperatureUnitsExec = temperatureUnitsRegex.exec(prevUnits);
    const newTemperatureUnitsExec = temperatureUnitsRegex.exec(newUnits);
    if (prevTemperatureUnitsExec !== null && newTemperatureUnitsExec !== null) {
        const [, prevTemperatureUnit, prevTimeUnit] = prevTemperatureUnitsExec;
        const [, newTemperatureUnit, newTimeUnit] = newTemperatureUnitsExec;
        if (!prevTimeUnit && !newTimeUnit) {
            if (newTemperatureUnit === TemperatureUnitsType.F) {
                valueToSave = Number(value) * 1.8 + 32;
            }
            else {
                valueToSave = (Number(value) - 32) / 1.8;
            }
        }
        else {
            const prevTimeConversationFactor = getConversionFactor(prevTimeUnit);
            const newTimeConversationFactor = getConversionFactor(newTimeUnit);
            if (prevTimeConversationFactor && newTimeConversationFactor) {
                if (newTemperatureUnit === prevTemperatureUnit) {
                    valueToSave = (Number(value) / prevTimeConversationFactor) * newTimeConversationFactor;
                }
                else if (newTemperatureUnit === TemperatureUnitsType.F) {
                    valueToSave = ((Number(value) * 1.8) / prevTimeConversationFactor) * newTimeConversationFactor;
                }
                else {
                    valueToSave = (Number(value) / 1.8 / prevTimeConversationFactor) * newTimeConversationFactor;
                }
            }
            else {
                return value;
            }
        }
    }
    else {
        const prevUnitsExec = nonTemperatureUnitsRegex.exec(prevUnits);
        const newUnitsExec = nonTemperatureUnitsRegex.exec(newUnits);
        if (prevUnitsExec !== null && newUnitsExec !== null) {
            const [, prevUnits, prevVolumeUnits, prevPerTimeUnits] = prevUnitsExec;
            const [, newUnits, newVolumeUnits, newPerTimeUnits] = newUnitsExec;
            const prevUnitsGroup = [prevUnits, prevVolumeUnits, prevPerTimeUnits];
            const filteredPrevUnitsGroup = prevUnitsGroup.filter(Boolean);
            const newUnitsGroup = [newUnits, newVolumeUnits, newPerTimeUnits];
            const filteredNewUnitsGroup = newUnitsGroup.filter(Boolean);
            if (filteredPrevUnitsGroup.length === 1 && filteredNewUnitsGroup.length === 1) {
                const prevConversationFactor = getConversionFactor(filteredPrevUnitsGroup[0]);
                const newConversationFactor = getConversionFactor(filteredNewUnitsGroup[0]);
                if (!prevConversationFactor || !newConversationFactor) {
                    return value;
                }
                if (prevPerTimeUnits && newPerTimeUnits) {
                    valueToSave = (Number(value) / prevConversationFactor) * newConversationFactor;
                }
                else {
                    valueToSave = (Number(value) * prevConversationFactor) / newConversationFactor;
                }
            }
            else {
                const prevConversationFactor = getConversionFactor(prevUnits);
                const newConversationFactor = getConversionFactor(newUnits);
                if (prevConversationFactor && newConversationFactor) {
                    valueToSave = (Number(value) * prevConversationFactor) / newConversationFactor;
                    const prevVolumeConversationFactor = getConversionFactor(prevVolumeUnits);
                    const newVolumeConversationFactor = getConversionFactor(newVolumeUnits);
                    if (prevVolumeConversationFactor && newVolumeConversationFactor) {
                        valueToSave = (valueToSave * prevVolumeConversationFactor) / newVolumeConversationFactor;
                    }
                    const prevPerTimeConversationFactor = getConversionFactor(prevPerTimeUnits);
                    const newPerTimeConversationFactor = getConversionFactor(newPerTimeUnits);
                    if (prevPerTimeConversationFactor && newPerTimeConversationFactor) {
                        valueToSave = (valueToSave / prevPerTimeConversationFactor) * newPerTimeConversationFactor;
                    }
                }
            }
        }
        else {
            return value;
        }
    }
    return !isNotEmpty(valueToSave) ? null : numberToFixedPointNotation(valueToSave);
};
export const convertValueBasedOnUnitsMutator = (args, state, tools) => (valueFormFieldName, unitsFormFieldName) => {
    const value = state.fields[valueFormFieldName]?.lastFieldState?.value ?? null;
    const formUnits = state.fields[unitsFormFieldName]?.lastFieldState?.value;
    const newUnits = typeof formUnits === "object" && "value" in formUnits ? formUnits?.value : formUnits;
    const prevUnits = typeof args[0] === "object" && "value" in args[0] ? args[0]?.value : args[0];
    tools.changeValue(state, valueFormFieldName, () => convertValueBasedOnUnits(value, prevUnits, newUnits));
};
export const reduceConvertValueBasedOnUnitsMutators = (mutators) => mutators.reduce((acc, { name, valueName }) => ({
    ...acc,
    [name]: (...args) => convertValueBasedOnUnitsMutator(...args)(`${valueName}.value`, `${valueName}.units`),
}), {});
